import React, {
  useCallback, useContext, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SelectAccount from 'screens/SelectAccount';
import handlePromise from 'utils/Promise';
import ProfileService from 'services/api/Profile';
import InvestmentAccountService from 'services/api/InvestmentAccount';
import { User } from 'types/User';
import { Account } from 'types/Account';
import { LoadingContext } from 'context/Loading';
import { ADMIN_ACCOUNTS } from 'constants/routes';
import {
  updateUserAccount, updateUserEmail, updateUserId, updateUserRole,
} from 'store/User';

function useSessionBootstrap() {
  const dispatch = useDispatch();
  const { toggleLoading } = useContext( LoadingContext );
  const navigate = useNavigate();
  const [showAccountModal, setAccountModal] = useState( false );
  const [profile, setProfile] = useState<User>();
  const [accounts, setAccounts] = useState<Account[]>( [] );

  const selectAccount = useCallback( ( accountId: string ) => {
    dispatch( updateUserAccount( accountId ) );
  }, [] );

  const bootstrap = useCallback( async () => {
    toggleLoading( true );
    const [, profileResponse, profileData] = await handlePromise( ProfileService.getProfile() );
    if ( profileResponse.ok ) setProfile( profileData );

    if ( profileData?.email ) dispatch( updateUserEmail( profileData?.email ) );

    if ( profileData?.isNew ) {
      navigate( '/force-change-password' );
      toggleLoading( false );
      return;
    }

    const [, accountsResponse, accountsData] = await handlePromise(
      InvestmentAccountService.getInvestmentAccounts(),
    );
    if ( accountsResponse.ok ) setAccounts( accountsData );

    dispatch( updateUserRole( profileData?.role ) );
    dispatch( updateUserId( profileData?.userId ) );

    if ( !accountsData?.length && profileData?.role === 'Admin' ) {
      selectAccount( 'admin' );
      navigate( ADMIN_ACCOUNTS );
    } else if ( accountsData?.length === 1 && profileData?.role !== 'Admin' ) {
      selectAccount( accountsData[0].cuentaDeInversionId );
    } else if ( ( profileData?.role === 'Admin' && !!accountsData?.length ) || ( accountsData?.length > 1 ) ) {
      setAccountModal( true );
    }
    toggleLoading( false );
  }, [] );

  const accountModal = (
    <SelectAccount
      show={showAccountModal}
      userRole={profile?.role}
      accounts={accounts}
      selectAccount={selectAccount}
    />
  );

  return { bootstrap, accountModal };
}

export default useSessionBootstrap;
